// 261005 E1-P1 — VỎ khung 5 chặng của tab "Chuyển thể" (thay `AdaptationTab` nguyên khối).
// Vỏ giữ: dữ liệu dùng chung, workbench (poll), job nền, hộp lỗi có mã, intent, URL `?stage=`, độ rộng 3 cột.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import type { WorkbenchStageKey } from '../../../types/adaptationWorkbench';
import AdaptationErrorModal from '../AdaptationErrorModal';
import { AdaptationErrorView, recoverRequest } from '../adaptationUtils';
import { intentStageOf } from './assistant/assistantIntent';
import type { AssistantIntent, AssistantRequest } from './assistant/assistantIntent';
import AssistantPanel from './assistant/AssistantPanel';
import Gutter from './Gutter';
import StageNav from './StageNav';
import ReviewBook from './ReviewBook';
import { enterReviewParams, goToStageParams, isReviewView, openEpisodeParams, reviewEntryView } from './reviewBookView';
import type { OpenEpisodeTarget } from './reviewBookView';
import { useSeasonReader } from './useSeasonReader';
import { seasonReaderCardView } from '../seasonReaderView';
import WorkbenchStrips, { NextLine } from './WorkbenchHeader';
import { useWorkbenchVars } from './useWorkbenchVars';
import { WorkbenchCtx, WorkbenchProvider } from './WorkbenchContext';
import { mergeTopMenu } from './topMenuView';
import type { TopMenuExtra, TopMenuSource } from './topMenuView';
import { useAdaptationData } from './useAdaptationData';
import { useBgJob } from './useBgJob';
import { EndedJob, endedJobText, pickBgCandidate, pickScriptCandidate, watcherShouldToast } from './jobTracking';
import { useScriptJob } from './useScriptJob';
import { useWorkbench } from './useWorkbench';
import { appendRunHoldsEdits, DISMISS_KEY, newEpisodeCount, readId, stageMarks, writeId } from './appendRunView';
import { shouldAutoOpenStopOne } from './newStoryRunView';
import { allWrittenPushed, chainOf, navRowsFor } from './stageNavView';
import WorkbenchTour from './WorkbenchTour';
import { parseTour, tourNavigate, tourParams, tourStep } from './tourView';
import type { TourWorld } from './tourView';
import { markReviewAutoOpened, reviewAutoOpened, shouldAutoOpenReview } from './reviewAutoOpenView';
import { ASSISTANT_BUTTON_LABEL, gridColsOf, initialAssistantOpen, saveAssistantOpen, SIDE_WIDTH } from './assistantRailView';
import { frameHeightOf } from './wbButtonView';
import type { AppendRunStatus } from '../../../types/adaptationWorkbench';
import {
  clampWidth, loadWidths, parseStageParam, resolveInitialStage, saveWidths, STAGE_LABEL, WIDTH_LIMITS, Widths,
  WidthKey, WorkbenchTarget, defaultWidths,
} from './workbenchView';
import SourceStage from './stages/SourceStage';
import SkeletonStage from './stages/SkeletonStage';
import LocalizeStage from './stages/LocalizeStage';
import SplitStage from './stages/SplitStage';
import ScriptStage from './stages/ScriptStage';
import './workbench.css';

interface Props { storyId: string; storyName?: string; storyLink?: string | null }

const STAGE_COMPONENT: Record<WorkbenchStageKey, React.FC> = {
  source: SourceStage, skeleton: SkeletonStage, localize: LocalizeStage, split: SplitStage, script: ScriptStage,
};

const AdaptationWorkbench: React.FC<Props> = ({ storyId, storyName, storyLink }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [errorView, setErrorView] = useState<AdaptationErrorView | null>(null);
  const [intent, setIntent] = useState<WorkbenchTarget | null>(null);
  const [dataVersion, setDataVersion] = useState(0);
  const [widths, setWidths] = useState<Widths>(() => loadWidths());
  // 3k-a (mockup Rev 3): Trợ lý GẬP mặc định, mở bằng nút «✦ Trợ lý» ở thanh trên; luôn khởi động đóng (không nhớ «mở» ở localStorage nữa; giá trị «1» cũ được dọn một lần). «Nhờ Trợ lý» tự mở, không ghi nhớ.
  const lsRef = useRef<Storage | null>((() => { try { return window.localStorage; } catch { return null; } })());
  const [assistantOpen, setAssistantOpen] = useState(() => initialAssistantOpen(lsRef.current));
  useEffect(() => { saveAssistantOpen(lsRef.current, false); }, []);
  // 3h3 (H3/H10): ai được đỏ — dải có nút chính bấm được (mỗi nguồn một khoá) · số tập đã tick ở Sổ.
  const [bandCtaMap, setBandCtaMap] = useState<Record<string, boolean>>({});
  const setBandCta = useCallback((k: string, v: boolean) => setBandCtaMap((m) => (m[k] === v ? m : { ...m, [k]: v })), []);
  const bandCta = Object.values(bandCtaMap).some(Boolean);
  const [reviewTick, setReviewTick] = useState(0);
  const [assistantIntent, setAssistantIntent] = useState<AssistantIntent | null>(null);
  const intentNonce = useRef(0);
  const [scriptEditing, setScriptEditing] = useState<number | null>(null);
  const [scriptSaveNonce, setScriptSaveNonce] = useState(0);
  const notifyScriptSaved = useCallback(() => setScriptSaveNonce((n) => n + 1), []);
  const [scriptEp, setScriptEp] = useState<{ ep: number | null; noScript: number | null }>({ ep: null, noScript: null });
  const initialStageFor = useRef<string | null>(null);
  const vars = useWorkbenchVars();
  // Chiều cao khung = phần màn còn lại dưới vị trí thật của `.wb` (trang ngoài không cuộn; chỉ các cột cuộn bên trong).
  const wbRef = useRef<HTMLDivElement>(null);
  // 3h1 (K3): dải nằm TRÊN cả lưới 3 cột ⇒ chiều cao khung đo từ ĐỈNH LƯỚI (không phải đỉnh `.wb`) và đo lại khi dải đổi chiều cao.
  const gridRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const [mainWidth, setMainWidth] = useState(1200);
  // 3k-c (SA NOTE 1): «⋯» có MỘT khe nhưng nhiều người đặt mục (bước · danh sách tập · tập đang mở) ⇒ mỗi nguồn một ô, vỏ gộp bằng hàm thuần `mergeTopMenu`.
  const [menuSources, setMenuSources] = useState<Partial<Record<TopMenuSource, TopMenuExtra[]>>>({});
  const setTopMenuSource = useCallback((src: TopMenuSource, items: TopMenuExtra[]) => setMenuSources((m) => (m[src] === undefined && items.length === 0 ? m : { ...m, [src]: items })), []);
  const setTopMenuExtra = useCallback((items: TopMenuExtra[]) => setTopMenuSource('stage', items), [setTopMenuSource]);
  const topMenuExtra = useMemo(() => mergeTopMenu(menuSources), [menuSources]);
  useEffect(() => {
    const el = mainRef.current; if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setMainWidth(Math.round(el.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [frameH, setFrameH] = useState(640);
  useLayoutEffect(() => {
    const measure = () => {
      const el = gridRef.current ?? wbRef.current; if (!el) return;
      // R4/F7: đỉnh lưới theo toạ độ TÀI LIỆU (cộng cuộn của cửa sổ và mọi tổ tiên) ⇒ không phụ thuộc đang cuộn tới đâu.
      let docTop = el.getBoundingClientRect().top + window.scrollY;
      for (let p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) docTop += p.scrollTop;   // R5: `documentElement.scrollTop` chính là `scrollY` — bỏ để không cộng đôi
      setFrameH(frameHeightOf(window.innerHeight, docTop));
    };
    measure();
    const t = window.setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    const band = topRef.current;
    const ro = band && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (band && ro) ro.observe(band);
    return () => { window.clearTimeout(t); window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, []);
  // 261005 E5 — khe để chặng chiếu danh sách tập / nút hành động vào thanh trái / thanh trên.
  const [slotSide, setSlotSide] = useState<HTMLElement | null>(null);
  const [slotSideFoot, setSlotSideFoot] = useState<HTMLElement | null>(null);
  const [slotTopSecondary, setSlotTopSecondary] = useState<HTMLElement | null>(null);
  const [slotTopPrimary, setSlotTopPrimary] = useState<HTMLElement | null>(null);
  const slots = useMemo(() => ({ side: slotSide, sideFoot: slotSideFoot, topSecondary: slotTopSecondary, topPrimary: slotTopPrimary }), [slotSide, slotSideFoot, slotTopSecondary, slotTopPrimary]);

  const data = useAdaptationData(storyId);
  const { reload } = data;
  const onJobsFinished = useCallback(() => { reload().then(() => setDataVersion((v) => v + 1)); }, [reload]);
  // F36: job kết thúc mà KHÔNG có chủ nào đang mount để tự báo (vd đã đổi sang chặng khác / tải lại trang) ⇒ vỏ báo.
  const stageRef = useRef<WorkbenchStageKey>('source');
  const onJobsEnded = useCallback((jobs: EndedJob[]) => {
    jobs.filter((j) => watcherShouldToast(j, stageRef.current)).forEach((j) => {
      const t = endedJobText(j);
      if (t.type === 'success') message.success(t.text, 5); else message.error(t.text, 8);
    });
  }, []);
  const wb = useWorkbench(storyId, onJobsFinished, onJobsEnded);
  const { refresh: refreshWorkbench } = wb;

  /** Sau MỌI thao tác ghi thành công: nạp lại workbench + dữ liệu chung (để chặng khác không hiện dữ liệu cũ). */
  const refreshAll = useCallback(async () => {
    const [rec] = await Promise.all([reload(), refreshWorkbench()]);
    setDataVersion((v) => v + 1);
    return rec;
  }, [reload, refreshWorkbench]);

  const showError = useCallback((v: AdaptationErrorView) => setErrorView(v), []);
  // F36: nguồn sự thật "đang chạy" là `workbench.jobs.running` của BE; hook cục bộ nhận nuôi job BE báo để không mất tiến độ / toast / làm mới sau tải lại.
  const runningJobs = wb.workbench?.jobs.running;
  const bgCandidate = useMemo(() => pickBgCandidate(runningJobs), [runningJobs]);
  const scriptCandidate = useMemo(() => pickScriptCandidate(runningJobs), [runningJobs]);
  const scriptJob = useScriptJob(storyId, refreshAll, showError, scriptCandidate);
  const bgJob = useBgJob(storyId, refreshAll, bgCandidate);

  // Job vừa bắt đầu ở phiên này: nạp workbench để dải job + khoá nút có hiệu lực ngay (poll chỉ chạy khi đã thấy running).
  const startedKey = `${bgJob.bgJob?.id ?? ''}|${scriptJob.scriptJob?.id ?? ''}`;
  useEffect(() => { if (startedKey !== '|') refreshWorkbench(); }, [startedKey, refreshWorkbench]);

  // ?stage= : thiếu / sai ⇒ trong lúc tải dùng `source`; tải xong → MỘT lần đặt theo nextAction (replace, không thêm lịch sử).
  const stageParam = parseStageParam(searchParams.get('stage'));
  const workbench = wb.workbench;
  // 261023 E3-P3a: Sổ duyệt (`?view=review`) KHÔNG phải chặng thứ 6 — mở Sổ thì không tự đặt `?stage=` theo nextAction (sẽ bật nhầm sang ⑤ và mất Sổ).
  const reviewing = isReviewView(searchParams.get('view'));
  useEffect(() => {
    if (!workbench || stageParam || reviewing || initialStageFor.current === storyId) return;
    initialStageFor.current = storyId;
    const s = resolveInitialStage(null, workbench.nextAction);
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('stage', s); return n; }, { replace: true });
  }, [workbench, stageParam, reviewing, storyId, setSearchParams]);
  useEffect(() => { initialStageFor.current = null; }, [storyId]);

  const stage: WorkbenchStageKey = stageParam ?? 'source';
  // Ở Sổ, toast «AI đọc cả mùa xong» vẫn phải nổ (ScriptStage không mounted) ⇒ coi như không ở ⑤.
  stageRef.current = reviewing && stage === 'script' ? 'source' : stage;

  // AI đọc cả mùa: MỘT hook ở vỏ cho ⑤ và Sổ duyệt. Không có kịch bản / không ở ⑤ lẫn Sổ ⇒ không gọi.
  const scriptCount = data.scripts.length;
  const readerToken = `${dataVersion}:${scriptJob.scriptJob ? 'w' : ''}:${scriptCount}`;
  const seasonReader = useSeasonReader(storyId, readerToken, (stage === 'script' || reviewing) && scriptCount > 0);
  const readerView = useMemo(() => seasonReaderCardView(seasonReader.resp), [seasonReader.resp]);
  const reviewEntry = useMemo(() => reviewEntryView(workbench, data.scripts, readerView), [workbench, data.scripts, readerView]);

  const goTo = useCallback((t: WorkbenchTarget) => {
    // `ep` chỉ có nghĩa ở ⑤: đổi sang chặng khác thì bỏ (cùng chặng thì giữ — intent có thể đặt lại). Rời Sổ ⇒ bỏ view/rf/from/scene/open (xem `goToStageParams`).
    setSearchParams((prev) => goToStageParams(prev, t.stage)); // giữ mode/tab
    setIntent(t.intent ? t : null);
  }, [setSearchParams]);
  const clearIntent = useCallback(() => setIntent(null), []);
  // 261023 E3-P3c1: chuỗi truyện mới tới STOP 1 ⇒ tự mở bước Khung xương (nơi có nút "Duyệt & chạy tiếp") — MỘT lần cho mỗi đợt.
  const stopOneOpenedFor = useRef<string | null>(null);
  useEffect(() => {
    const run = wb.workbench?.appendRun;
    if (!shouldAutoOpenStopOne(run, stopOneOpenedFor.current) || !run) return;
    stopOneOpenedFor.current = run.id;
    if (stageRef.current !== 'skeleton' && !isReviewView(searchParams.get('view'))) goTo({ stage: 'skeleton', intent: 'approve' });
  }, [wb.workbench?.appendRun, goTo, searchParams]);
  const openReview = useCallback(() => { setIntent(null); setSearchParams((prev) => enterReviewParams(prev)); }, [setSearchParams]);
  // Mở một tập từ Sổ = ĐẨY lịch sử (nút Back của trình duyệt về lại đúng Sổ + bộ lọc).
  const openEpisodeFromReview = useCallback((t: OpenEpisodeTarget) => { setIntent(null); setSearchParams((prev) => openEpisodeParams(prev, t)); }, [setSearchParams]);

  // 3h2 (H7): máy viết xong (chuỗi vừa DONE trước mắt người đang mở bàn làm việc) ⇒ tự mở Sổ duyệt MỘT lần theo run.id; người rời Sổ không bị kéo lại.
  const prevRun = useRef<{ id: string; status: AppendRunStatus } | null>(null);
  useEffect(() => { prevRun.current = null; }, [storyId]);
  useEffect(() => {
    const run = wb.workbench?.appendRun ?? null;
    const prev = prevRun.current;
    prevRun.current = run ? { id: run.id, status: run.status } : null;
    if (!run || !wb.workbench) return;
    let store: Storage | null = null;
    try { store = window.sessionStorage; } catch { store = null; }
    if (shouldAutoOpenReview({
      prevStatus: prev && prev.id === run.id ? prev.status : null, run,
      atReview: isReviewView(searchParams.get('view')) || searchParams.get('from') === 'review',
      hasWritten: wb.workbench.episodes.some((e) => e.written === 'draft'), alreadyOpened: reviewAutoOpened(store, run.id),
    })) { markReviewAutoOpened(store, run.id); openReview(); }
  }, [wb.workbench, searchParams, openReview]);

  // 3h2 (H6): «? Hướng dẫn» — trạng thái ở query `tour=n` (mở thẳng được, KHÔNG tự bật). Đổi mục ⇒ tự đưa màn tới đúng chỗ; Thoát ⇒ quay lại chỗ đang đứng lúc bấm.
  const tourN = parseTour(searchParams.get('tour'));
  const tourStepV = tourStep(tourN);
  const tourBack = useRef<string | null>(null);
  const tourWorldRef = useRef<TourWorld>({ hasReview: false, firstNotRunStage: null, firstWrittenEp: null });
  const goTour = useCallback((n: number | null) => setSearchParams((prev) => tourParams(prev, n), { replace: true }), [setSearchParams]);
  const exitTour = useCallback(() => setSearchParams((prev) => {
    const base = tourBack.current !== null ? new URLSearchParams(tourBack.current) : new URLSearchParams(prev);
    tourBack.current = null; base.delete('tour'); return base;
  }, { replace: true }), [setSearchParams]);
  useEffect(() => {
    if (!tourStepV) return;
    setSearchParams((prev) => tourNavigate(prev, tourStepV, tourWorldRef.current), { replace: true });
    // chỉ đổi màn khi ĐỔI MỤC (world đọc qua ref để không lặp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourN]);

  // 261005 E4-P4b: chuỗi "Xử lý phần mới" RUNNING (kể cả khoảng hở giữa bước) khoá nút ghi — BE cũng chặn bằng 409 APPEND_RUN_ACTIVE.
  // 261015 K-b: gián đoạn CÓ LỖI (`RUNNING + error`) KHÔNG khoá — người phải sửa được nguyên nhân rồi "Chạy tiếp" (xem `appendRunHoldsEdits`).
  const busy = !!bgJob.bgJob || !!bgJob.starting || !!scriptJob.scriptJob || scriptJob.starting
    || (workbench?.jobs.running.length ?? 0) > 0 || appendRunHoldsEdits(workbench?.appendRun);

  // Dải "Phần mới" đã đóng (nhớ theo id đợt ở localStorage, có try/catch).
  const [dismissedId, setDismissedId] = useState<string | null>(() => readId(DISMISS_KEY(storyId)));
  useEffect(() => { setDismissedId(readId(DISMISS_KEY(storyId))); }, [storyId]);
  const dismissAppend = useCallback((id: string) => { writeId(DISMISS_KEY(storyId), id); setDismissedId(id); }, [storyId]);

  const setWidth = useCallback((key: WidthKey, px: number) => {
    setWidths((w) => { const n = { ...w, [key]: clampWidth(key, px) }; saveWidths(n); return n; });
  }, []);
  const resetWidth = useCallback((key: WidthKey) => setWidth(key, defaultWidths()[key]), [setWidth]);

  const setScriptEpisode = useCallback((ep: number | null, noScriptEp: number | null = null) => {
    setScriptEp((cur) => (cur.ep === ep && cur.noScript === noScriptEp ? cur : { ep, noScript: noScriptEp }));
  }, []);

  const requestAssistant = useCallback((r: AssistantRequest) => {
    intentNonce.current += 1;
    setAssistantIntent({ ...r, nonce: intentNonce.current });
    setAssistantOpen(true); // đang Thu gọn ⇒ mở lại (nháp đã mất khi Thu gọn — Y4 chấp nhận)
  }, []);
  const clearAssistantIntent = useCallback((nonce?: number) => {
    setAssistantIntent((cur) => (cur && (nonce === undefined || cur.nonce === nonce) ? null : cur));
  }, []);
  // Intent chỉ có nghĩa ở CHẶNG CỦA NÓ (⑤ mặc định; ② khi `stageKey:'skeleton'` — E3-P1b) và đúng truyện: sang chặng KHÁC (mọi đường: goTo, Back, sửa URL) hoặc đổi truyện ⇒ xoá.
  useEffect(() => { setAssistantIntent((cur) => (cur && intentStageOf(cur) !== stage ? null : cur)); }, [stage]);
  useEffect(() => { setAssistantIntent(null); }, [storyId]);
  // Tập đích không còn kịch bản (A3) ⇒ xoá, khỏi treo rồi bật lại ở lần mở sau.
  useEffect(() => { setAssistantIntent((cur) => (cur && cur.episode !== undefined && scriptEp.noScript === cur.episode ? null : cur)); }, [scriptEp.noScript]);

  // 3h1 (H4): cột bước tính từ dữ liệu workbench + chuỗi (hàm thuần `stageNavRows`); chuỗi «Phần mới» (APPEND) giữ dấu chip cũ qua `applyAppendMarks`.
  const navRows = useMemo(() => {
    if (!workbench) return null;
    const marks = stageMarks(workbench, { newEpisodes: newEpisodeCount(workbench.appendRun, workbench.outlineFlags.episodesListed ?? 0), dismissedId });
    return navRowsFor(workbench, { dismissedId, reviewNeed: reviewEntry.needCount, allPushed: allWrittenPushed(workbench.episodes), rawChapters: data.rawChapters }, marks);
  }, [workbench, dismissedId, reviewEntry.needCount, data.rawChapters]);
  const chainActive = !!chainOf(workbench, dismissedId);
  tourWorldRef.current = {
    hasReview: reviewEntry.visible,
    firstNotRunStage: navRows?.find((r) => r.byChain && r.key !== 'source' && (r.status === 'notRun' || r.status === 'running'))?.key ?? null,
    firstWrittenEp: workbench?.episodes.find((e) => e.written === 'draft')?.ep ?? null,
  };
  const startTour = () => {
    const go = () => { const p = new URLSearchParams(searchParams); p.delete('tour'); tourBack.current = p.toString(); goTour(1); };
    if (scriptEditing !== null) {
      Modal.confirm({ title: 'Bạn đang sửa dở một tập', content: 'Hướng dẫn sẽ chuyển qua các màn khác; phần đang sửa chưa lưu sẽ mất. Lưu trước rồi mở Hướng dẫn nhé.', okText: 'Vẫn mở Hướng dẫn', cancelText: 'Ở lại sửa', okButtonProps: { danger: true }, onOk: go });
    } else go();
  };
  const startTourRef = useRef(startTour);
  startTourRef.current = startTour;
  const startTourStable = useCallback(() => startTourRef.current(), []);

  const ctx: WorkbenchCtx = useMemo(() => ({
    storyId, storyName: storyName ?? workbench?.story.name, storyLink, data, workbench, refreshAll, dataVersion, showError,
    intent, clearIntent, goTo, scriptJob, bgJob, busy,
    stageState: (s) => workbench?.stages.find((x) => x.key === s)?.state,
    listWidth: widths.list, setListWidth: (px) => setWidth('list', px), resetListWidth: () => resetWidth('list'),
    scriptEpisode: scriptEp.ep, scriptEpisodeNoScript: scriptEp.noScript, setScriptEpisode,
    assistantIntent, requestAssistant, clearAssistantIntent, scriptEditing, setScriptEditing, scriptSaveNonce, notifyScriptSaved, appendDismissedId: dismissedId, dismissAppend, slots, assistantOpen, mainWidth, setTopMenuExtra, setTopMenuSource, topMenuExtra,
    seasonReader, reviewing, openReview, openEpisodeFromReview, reviewNeed: reviewEntry.needCount, navRows, chainActive, bandCta, setBandCta, reviewTick, setReviewTick,
    startTour: startTourStable,
  }), [setTopMenuExtra, setTopMenuSource, startTourStable, navRows, chainActive, bandCta, setBandCta, reviewTick, reviewEntry.needCount, seasonReader, reviewing, openReview, openEpisodeFromReview, slots, assistantOpen, mainWidth, topMenuExtra, storyId, storyName, storyLink, data, workbench, refreshAll, dataVersion, showError, intent, clearIntent, goTo, scriptJob, bgJob, busy, widths.list, setWidth, resetWidth, scriptEp, setScriptEpisode, assistantIntent, requestAssistant, clearAssistantIntent, scriptEditing, scriptSaveNonce, notifyScriptSaved, dismissedId, dismissAppend]);

  const StageComponent = STAGE_COMPONENT[stage];
  const recover = errorView?.recoverFrom !== undefined ? recoverRequest(data.scripts, errorView.recoverFrom) : null;
  const jobBusy = busy;

  const crumbEp = !reviewing && stage === 'script' ? (scriptEp.ep ?? scriptEp.noScript) : null;
  const gridCols = gridColsOf(assistantOpen, widths.assistant, SIDE_WIDTH);

  return (
    <WorkbenchProvider value={ctx}>
      <div className="wb" style={vars} ref={wbRef}>
        {/* 3h1 (K3): dải «máy tự chạy ba chặng» + các dải job nằm TRÊN cả lưới. 3k-c: cột bước bên trái TRỞ LẠI (không còn dải 5 bước ngang). */}
        <div className="wb-top-strips" ref={topRef}>
          <WorkbenchStrips
            workbench={workbench} loadError={wb.error || data.loadError} onRetry={refreshAll} bgJob={bgJob} scriptJob={scriptJob}
            appendDismissedId={dismissedId} onDismissAppend={dismissAppend} stage={stage}
          />
        </div>
        <div className="wb-grid" style={{ gridTemplateColumns: gridCols, height: frameH }} ref={gridRef}>
          <aside className="wb-side" aria-label="Các bước và danh sách tập">
            <StageNav
              rows={navRows} current={reviewing ? null : stage}
              onSelect={(k) => goTo({ stage: k })} hints={reviewEntry.hintCount}
              review={{ visible: reviewEntry.visible || reviewing, active: reviewing, needCount: reviewEntry.needCount, onOpen: openReview }}
            />
            <div className="wb-side-slot" ref={setSlotSide} />
            <div className="wb-side-foot" ref={setSlotSideFoot} />
          </aside>

          <main className="wb-main" ref={mainRef}>
            <div className="wb-topbar">
              <span className="wb-crumb">
                {reviewing ? 'Sổ duyệt' : STAGE_LABEL[stage]}
                {crumbEp !== null && <><span className="wb-crumb-sep">/</span><b>Tập {crumbEp}</b></>}
              </span>
              <NextLine workbench={workbench} onGo={goTo} hidden={mainWidth < 820 || reviewing || chainActive} />
              <div className="wb-top-actions">
                <span className="wb-top-group" ref={setSlotTopSecondary} />
                <Button size="small" className={`wb-assist-btn${assistantOpen ? ' wb-assist-on' : ''}`} data-tour="assist" aria-pressed={assistantOpen}
                  title={assistantOpen ? 'Đóng Trợ lý AI' : 'Mở Trợ lý AI'} onClick={() => setAssistantOpen((v) => !v)}>{ASSISTANT_BUTTON_LABEL}</Button>
                <span className="wb-top-group" ref={setSlotTopPrimary} />
                {/* Sổ duyệt không có «⋯» của bước ⇒ «? Hướng dẫn» vẫn ở đây; mọi bước khác có «Hướng dẫn» trong «⋯». */}
                {reviewing && <Button size="small" onClick={startTour} title="Xem hướng dẫn từng phần của màn này">? Hướng dẫn</Button>}
              </div>
            </div>
            <div className="wb-scroll">
              <div className={`wb-content${stage === 'script' && !reviewing ? '' : ' wb-wide'}`}>
                <div className="wb-pane">
                  {data.loading ? <div className="wb-loading"><Spin /></div> : (reviewing ? <ReviewBook key={`${storyId}:review`} /> : <StageComponent key={`${storyId}:${stage}`} />)}
                </div>
              </div>
            </div>
          </main>

          {assistantOpen && (
            <div className="wb-aside" style={{ width: widths.assistant }}>
              <Gutter
                label="khung Trợ lý AI" value={widths.assistant} min={WIDTH_LIMITS.assistant.min} max={WIDTH_LIMITS.assistant.max} direction={-1}
                onChange={(px) => setWidth('assistant', px)} onReset={() => resetWidth('assistant')}
              />
              <AssistantPanel stage={stage} onCollapse={() => setAssistantOpen(false)} />
            </div>
          )}
        </div>
      </div>

      {tourStepV && (
        <WorkbenchTour
          step={tourStepV} onExit={exitTour}
          onPrev={() => goTour(Math.max(1, (tourN ?? 1) - 1))} onNext={() => goTour(Math.min(10, (tourN ?? 1) + 1))}
        />
      )}

      <AdaptationErrorModal
        error={errorView} onClose={() => setErrorView(null)} busy={jobBusy}
        onOpenOverview={() => { setErrorView(null); goTo({ stage: 'skeleton', intent: 'openOverview' }); }}
        recover={recover ? {
          label: `Viết lại tuần tự từ tập ${recover.from}`,
          description: `Sẽ viết lại tập ${recover.from}${recover.to > recover.from ? `–${recover.to}` : ''} (${recover.to - recover.from + 1} tập) theo thứ tự, ghi đè kịch bản hiện có.`,
          onClick: () => { const r = recover; setErrorView(null); scriptJob.writeScripts(r); },
          disabled: jobBusy,
        } : null}
      />
    </WorkbenchProvider>
  );
};

export default AdaptationWorkbench;
